<template>
  <div>
    <div class="space-y-4">
      <div class="grid gap-6">
        <LoginButtons
          :show-social-buttons="enableSocialButtons"
          :disabled="submittingForm"
          :large="showLargeLogin"
          labelIntro="Register with"
          @continueWith="registerWith"
        />
        <LoginButton
          v-if="showLargeLogin"
          type="button"
          :large="true"
          label="email"
          labelIntro="Register with"
          class="btn-tertiary w-full"
          :disabled="submittingForm"
          @click="mailLoginOpen = true"
        >
          <EmailIcon slot="icon" class="w-5 h-5" :aria-hidden="true" />
        </LoginButton>
      </div>

      <div v-if="!showLargeLogin" class="space-y-4">
        <form class="grid gap-3" @submit.prevent>
          <TextInput
            v-model.trim="email"
            required
            type="email"
            :label="fields.email.label"
            :disabled="submittingForm"
            autocomplete="email"
            :placeholder="fields.email.placeholder"
            :status="fields.email.status"
            :status-message="fields.email.message"
            :prompt="fields.email.prompt"
            @enter="submitForm"
          />
          <TextInput
            v-model="password"
            required
            maxlength="100"
            type="password"
            :label="fields.password.label"
            :disabled="submittingForm"
            autocomplete="current-password"
            :placeholder="fields.password.placeholder"
            :status="fields.password.status"
            :status-message="fields.password.message"
            :prompt="fields.password.prompt"
            @enter="submitForm"
          />
          <!-- extra fields, handled outside of this component -->
          <slot />
          <p v-show="serverErrorMessage" class="text-xs text-red-600">
            {{ serverErrorMessage }}
          </p>
        </form>
        <div v-show="showContinueButton">
          <button
            type="button"
            class="btn btn-primary w-full text-sm"
            :disabled="submittingForm"
            :loading="submittingForm"
            @click="submitForm"
          >
            {{ buttonText || page.button }}
            <ArrowRight class="btn-icon-right" />
          </button>
        </div>
      </div>
    </div>
    <p class="mt-2" :class="`text-${promptAlign}`">
      <button
        type="button"
        class="mx-auto text-xs text-gray-500 hover:text-blue-600 focus:text-cool-gray-700 active:text-cool-gray-700 transition ease-in-out duration-150"
        @click="logHasAccount"
        v-html="page.prompt"
      ></button>
    </p>
  </div>
</template>

<script>
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators'
import page from '@/content/auth-pages/register'
import { EventBus, Events } from '@/lib/events'
import authForm from '@/mixins/auth-form'
import logger from '@/lib/logger'
import { Auth } from 'aws-amplify'
import { useUser } from '~/lib/hooks'

export default {
  mixins: [authForm],

  props: {
    buttonText: { type: String, default: '' },
  },

  data: () => ({
    email: '',
    password: '',
    serverErrorMessage: null,
    submittingForm: false,
    page,
    mailLoginOpen: false,
  }),

  validations() {
    const validations = {
      email: { required, email, maxLength: maxLength(128) },
      password: { required, minLength: minLength(8), maxLength: maxLength(99) },
    }

    return validations
  },

  computed: {
    showLargeLogin() {
      return this.flags.enable_big_social_login && !this.mailLoginOpen
    },
  },

  methods: {
    /**
     * Handle presing the "enter" key.
     */
    submitForm() {
      if (this.handleByParent) {
        this.$emit('register')
      } else {
        this.register()
      }
    },

    async registerWith(provider) {
      const query = {
        app_return_path: this.$route.query.app_return_path || '/editor/colors',
        return: this.$route.path,
        ...this.$route.query,
        ...this.oauthParams,
        provider: provider,
        action: 'sign_up',
      }

      const auth = await this.$getAuthInstance()

      auth.federatedSignIn({
        provider,
        customState: new URLSearchParams(query).toString(),
      })
    },

    logHasAccount() {
      this.$emit('goToLogin')
    },

    /**
     * Register the user!
     *
     * @param {boolean} throwError If we want to throw the error to handle in a parent component
     */
    async register(throwError = false) {
      this.serverErrorMessage = ''

      if (this.$invalidInput() || this.submittingForm) return

      this.submittingForm = true

      try {
        this.serverErrorMessage = null

        await Auth.signUp({
          username: this.email.toLowerCase(),
          password: this.password,
          attributes: {
            given_name: '',
            family_name: '',
            email: this.email.toLowerCase(),
          },
        })

        // Our user was auto confirmed, but we need to signIn
        // to gain access to the session
        await Auth.signIn({
          username: this.email.toLowerCase(),
          password: this.password,
        })

        this.$emit('register')
      } catch (error) {
        logger.debug('Register: failed to create user', error)
        this.serverErrorMessage = error.message

        if (throwError) {
          throw error
        }

        this.submittingForm = false
      }
    },
  },
}
</script>
